import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { GetSalesInsightsAggregatedRequest, SalesInsights, SalesInsightsCompareResponse, useSalesInsightsCompare } from '../../../../api/indicators';
import { DashboardFiltersContext } from '../filters/DashboardFiltersContext';
import { dateRange, formatDateTimeParam } from '../../../../helpers/dateUtils';
import { DashboardContext } from '../../DashboardContext';
import { calculatePercentChanges } from '../../dashboardHelpers';
import ProgressChart from '../charts/progressChart/ProgressChart';
import NumericChartCompared from '../charts/numericChart/NumericChartCompared';


const ComparedSalesInsightAreaSales = () => {
    const { t } = useTranslation();
    const { filters, dateRangeConfig, chips } = useContext(DashboardFiltersContext);
    const { companyConfig, refreshDate } = useContext(DashboardContext);


    const [params, setParams] = useState<GetSalesInsightsAggregatedRequest>({
        fields: 'netSales,totalSales,totalTax,totalDiscount,grossRevenue',
        startDateTime: formatDateTimeParam(filters.periodFrom ? filters.periodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate),
        endDateTime: formatDateTimeParam(filters.periodTo ? filters.periodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate),
        compareStartDateTime: formatDateTimeParam(filters.comparePeriodFrom ? filters.comparePeriodFrom : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate),
        compareEndDateTime: formatDateTimeParam(filters.comparePeriodTo ? filters.comparePeriodTo : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate),
        locations: filters?.locations?.join(',') ? filters?.locations?.join(',') : companyConfig?.locations?.map(location => location.id).join(','),
        locationArea: filters?.locationArea?.id
    });

    const {
        data: comparedSalesInsights,
        isLoading,
        isError,
        refetch: fetchSalesInsights
    } = useSalesInsightsCompare<SalesInsightsCompareResponse>(params, 'compared-sales-insights');


    const getValues = useCallback((field: keyof SalesInsights, comparedField: keyof SalesInsights): [number, number] => {
        const current = comparedSalesInsights?.salesInsights?.[field] ?? 0;
        const compared = comparedSalesInsights?.comparedSalesInsights?.[comparedField] ?? 0;
        return [current, compared];
    }, [comparedSalesInsights]);

    const { percent: totalSalesIncPercent, percentCompared: totalSalesIncPercentCompared } = useMemo(() => {
        const [current, compared] = getValues('totalSalesIncTax', 'totalSalesIncTax');
        return calculatePercentChanges(current, compared);
    }, [
        comparedSalesInsights?.salesInsights.netSalesIncTax,
        comparedSalesInsights?.comparedSalesInsights.netSalesIncTax
    ]);

    const { percent: salesExcPercent, percentCompared: salesExcPercentCompared } = useMemo(() => {
        const [current, compared] = getValues('netSalesExcTax', 'netSalesExcTax');
        return calculatePercentChanges(current, compared);
    }, [
        comparedSalesInsights?.salesInsights.netSalesExcTax,
        comparedSalesInsights?.comparedSalesInsights.netSalesExcTax
    ]);

    useEffect(() => {
        if (refreshDate != null) {
            fetchSalesInsights();
        }
    }, [refreshDate]);

    useEffect(() => {
        setParams({
            ...params,
            startDateTime: filters.periodFrom ? filters.periodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate,
            endDateTime: filters.periodTo ? filters.periodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate,
            compareStartDateTime: formatDateTimeParam(filters.comparePeriodFrom ? filters.comparePeriodFrom : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate),
            compareEndDateTime: formatDateTimeParam(filters.comparePeriodTo ? filters.comparePeriodTo : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate),
            locations: filters?.locations?.join(',') ? filters?.locations?.join(',') : companyConfig?.locations?.map(location => location.id).join(','),
            locationArea: filters?.locationArea?.id
        });
    }, [filters]);

    useEffect(() => { fetchSalesInsights(); }, [params]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ marginBottom: '4px' }}>
                <NumericChartCompared
                    title={t("screens.dashboarding.charts.sales")}
                    subtitle={t("screens.dashboarding.charts.includingTax")}
                    currency={comparedSalesInsights?.currency}
                    currentDateFrom={filters.periodFrom ? filters.periodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate}
                    currentDateTo={filters.periodTo ? filters.periodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate}
                    comparedDateFrom={filters.comparePeriodFrom ? filters.comparePeriodFrom : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate}
                    comparedDateTo={filters.comparePeriodTo ? filters.comparePeriodTo : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate}
                    data={comparedSalesInsights?.salesInsights.totalSalesIncTax || 0}
                    dataCompared={comparedSalesInsights?.comparedSalesInsights.totalSalesIncTax || 0}
                    percent={totalSalesIncPercent}
                    percentCompared={totalSalesIncPercentCompared}
                    isLoading={isLoading}
                    isError={isError}
                    linkChart={`${process.env.REACT_APP_LINK_BACK_OFFICE}Pages/Reporting/SalesBreakdown.aspx`}
                    buttonText={t("screens.dashboarding.charts.salesOverview")}
                />
            </Box>
            <Box sx={{ marginTop: '4px' }}>
                <NumericChartCompared
                    title={t("screens.dashboarding.charts.netSales")}
                    subtitle={t("screens.dashboarding.charts.excludingTax")}
                    currency={comparedSalesInsights?.currency}
                    currentDateFrom={filters.periodFrom ? filters.periodFrom : dateRange("today", undefined, undefined, undefined, dateRangeConfig).startDate}
                    currentDateTo={filters.periodTo ? filters.periodTo : dateRange("today", undefined, undefined, undefined, dateRangeConfig).endDate}
                    comparedDateFrom={filters.comparePeriodFrom ? filters.comparePeriodFrom : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).startDate}
                    comparedDateTo={filters.comparePeriodTo ? filters.comparePeriodTo : dateRange("yesterday", undefined, undefined, undefined, dateRangeConfig).endDate}
                    data={comparedSalesInsights?.salesInsights.netSalesIncTax}
                    dataCompared={comparedSalesInsights?.comparedSalesInsights.totalSalesIncTax}
                    percent={salesExcPercent}
                    percentCompared={salesExcPercentCompared}
                    isLoading={isLoading}
                    isError={isError}
                    linkChart={`${process.env.REACT_APP_LINK_BACK_OFFICE}Pages/Reporting/SalesBreakdown.aspx`}
                    buttonText={t("screens.dashboarding.charts.salesOverview")}
                />
            </Box>
        </Box>
    )
}

export default ComparedSalesInsightAreaSales;
